import { useRef, useState } from 'react'
import { FileAudio, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { api } from '@/lib/api'

interface Props {
  audioUrl: string
  audioFilename: string
  onUpload: (url: string, filename: string) => void
  onClear: () => void
}

interface UploadResponse {
  url: string
  filename: string
}

const MAX_BYTES = 10 * 1024 * 1024
const EXTENSIONS = ['mp3', 'wav', 'ogg', 'm4a']

// Browsers report audio types inconsistently (audio/x-wav, audio/mp3, or nothing
// for a dragged file), so the extension decides, as on the server.
function extensionOf(name: string) {
  const dot = name.lastIndexOf('.')
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase()
}

function carriesFiles(e: React.DragEvent) {
  return Array.from(e.dataTransfer.types).includes('Files')
}

/**
 * Audio of a flow node: drop a file or click to choose it, listen, replace, remove.
 * Upload via `/api/ivr/upload-audio/` (mp3/wav/ogg/m4a, 10 MB max). Removing only
 * clears the node: the stored file stays, a duplicated flow may still play it.
 */
export function AudioUploadField({
  audioUrl,
  audioFilename,
  onUpload,
  onClear,
}: Props) {
  const { t } = useTranslation()
  const [uploading, setUploading] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  // dragenter/dragleave also fire on every child: count them to know when the file leaves.
  const dragDepth = useRef(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const send = async (file: File) => {
    if (uploading) return
    if (!EXTENSIONS.includes(extensionOf(file.name))) {
      setError(t('manager.flows.editor.audio.formatNotSupported'))
      return
    }
    if (file.size > MAX_BYTES) {
      setError(t('manager.flows.editor.audio.fileTooLarge'))
      return
    }

    setError('')
    setUploading(file.name)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post<UploadResponse>(
        '/api/ivr/upload-audio/',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      onUpload(data.url, data.filename || file.name)
    } catch (err: unknown) {
      const code = (err as { response?: { data?: { code?: string } } })
        .response?.data?.code
      setError(
        code === 'format'
          ? t('manager.flows.editor.audio.formatNotSupported')
          : code === 'too_large'
            ? t('manager.flows.editor.audio.fileTooLarge')
            : t('manager.flows.editor.audio.uploadError'),
      )
    } finally {
      setUploading(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const choose = () => {
    if (!uploading) fileInputRef.current?.click()
  }

  const dropHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      dragDepth.current += 1
      setDragOver(true)
    },
    onDragOver: (e: React.DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      e.dataTransfer.dropEffect = uploading ? 'none' : 'copy'
    },
    onDragLeave: (e: React.DragEvent) => {
      if (!carriesFiles(e)) return
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) setDragOver(false)
    },
    onDrop: (e: React.DragEvent) => {
      if (!carriesFiles(e)) return
      e.preventDefault()
      e.stopPropagation()
      dragDepth.current = 0
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) void send(file)
    },
  }

  const actionClass =
    'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-medium ring-1 ring-inset transition-colors disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <div className="space-y-2">
      <Label>{t('manager.flows.editor.audio.audioFile')}</Label>

      {audioUrl ? (
        <div
          {...dropHandlers}
          className={cn(
            'rounded-lg p-3 space-y-3 ring-1 ring-inset transition-colors',
            dragOver
              ? 'bg-blue-500/[0.12] ring-blue-400/60'
              : 'bg-white/[0.04] ring-white/[0.08]',
          )}
        >
          <div className="flex items-center gap-2 min-w-0 text-sm">
            {uploading ? (
              <Loader2 className="h-4 w-4 shrink-0 text-blue-400 animate-spin" />
            ) : (
              <FileAudio className="h-4 w-4 shrink-0 text-emerald-400" />
            )}
            <span
              className="truncate text-white/85"
              title={uploading ?? audioFilename}
            >
              {uploading
                ? t('manager.flows.editor.audio.uploadingFile', { name: uploading })
                : dragOver
                  ? t('manager.flows.editor.audio.dropToReplace')
                  : audioFilename || t('manager.flows.editor.audio.audioUploaded')}
            </span>
          </div>

          <audio
            key={audioUrl}
            src={audioUrl}
            controls
            preload="metadata"
            className="w-full h-9 [color-scheme:dark]"
          />

          <div className="flex gap-2">
            <button
              type="button"
              onClick={choose}
              disabled={!!uploading}
              className={cn(
                actionClass,
                'text-white/80 bg-white/[0.06] ring-white/[0.12] hover:bg-white/[0.1] hover:text-white',
              )}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t('manager.flows.editor.audio.replace')}
            </button>
            <button
              type="button"
              onClick={() => {
                setError('')
                onClear()
              }}
              disabled={!!uploading}
              className={cn(
                actionClass,
                'text-red-400/90 bg-red-500/[0.06] ring-red-500/20 hover:bg-red-500/[0.12] hover:text-red-400',
              )}
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('manager.flows.editor.audio.delete')}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          {...dropHandlers}
          onClick={choose}
          disabled={!!uploading}
          className={cn(
            'w-full flex flex-col items-center justify-center gap-1.5 px-4 py-6 rounded-lg border-2 border-dashed text-center transition-colors',
            uploading
              ? 'border-white/[0.12] bg-white/[0.03] cursor-wait'
              : dragOver
                ? 'border-blue-400 bg-blue-500/[0.12]'
                : 'border-white/[0.14] hover:border-blue-400/70 hover:bg-blue-500/[0.06]',
          )}
        >
          {uploading ? (
            <>
              <Loader2 className="h-7 w-7 text-blue-400 animate-spin" />
              <span className="text-sm text-white/70 max-w-full truncate">
                {t('manager.flows.editor.audio.uploadingFile', { name: uploading })}
              </span>
            </>
          ) : (
            <>
              <Upload
                className={cn('h-7 w-7', dragOver ? 'text-blue-400' : 'text-white/40')}
              />
              <span className="text-sm font-medium text-white/80">
                {dragOver
                  ? t('manager.flows.editor.audio.dropToUpload')
                  : t('manager.flows.editor.audio.dropHere')}
              </span>
              {!dragOver && (
                <span className="text-xs text-white/45">
                  {t('manager.flows.editor.audio.orClick')}
                </span>
              )}
              <span className="text-[11px] text-white/30">
                {t('manager.flows.editor.audio.acceptedFormats')}
              </span>
            </>
          )}
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".mp3,.wav,.ogg,.m4a"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void send(file)
        }}
        className="hidden"
      />

      {error && (
        <div role="alert" className="text-[13px] text-red-400">
          {error}
        </div>
      )}
    </div>
  )
}
